import React, { useState, useMemo } from 'react';
import VehicleStatusBadge from './VehicleStatusBadge';
import VehicleFilters from './VehicleFilters';
import BulkActions from './BulkActions';
import VehicleAvailabilityModal from './VehicleAvailabilityModal';
import {
  Vehicle,
  VehicleFilters as VehicleFiltersType,
  VehicleStatus,
  VehicleAvailability,
} from '../../types/vehicle';
import { vehicleService } from '../../services/vehicleService';

interface VehicleListingProps {
  vehicles: Vehicle[];
  loading?: boolean;
  onVehicleSelect?: (vehicle: Vehicle) => void;
  onVehicleEdit?: (vehicle: Vehicle) => void;
  onVehicleDelete?: (vehicleId: string) => void;
  onStatusUpdate?: (vehicleId: string, status: VehicleStatus) => void;
  availability?: VehicleAvailability[];
}

const VehicleListing: React.FC<VehicleListingProps> = ({
  vehicles,
  loading = false,
  onVehicleSelect,
  onVehicleEdit,
  onVehicleDelete,
  onStatusUpdate,
  availability = [],
}) => {
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<VehicleFiltersType>({});
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedAvailability, setSelectedAvailability] =
    useState<VehicleAvailability | null>(null);

  const { availableBrands, availableLocations } = useMemo(() => {
    const brands = Array.from(
      new Set(vehicles.map((v) => v.brand).filter(Boolean)),
    ).sort();
    const locations = Array.from(
      new Set(vehicles.map((v) => v.location).filter(Boolean)),
    ).sort() as string[];
    return { availableBrands: brands, availableLocations: locations };
  }, [vehicles]);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      if (filters.status && vehicle.status !== filters.status) return false;
      if (
        filters.location &&
        !(vehicle.location || '')
          .toLowerCase()
          .includes(filters.location.toLowerCase())
      ) {
        return false;
      }
      if (
        filters.brand &&
        !(vehicle.brand || '').toLowerCase().includes(filters.brand.toLowerCase())
      ) {
        return false;
      }
      if (
        filters.model &&
        !(vehicle.model || '').toLowerCase().includes(filters.model.toLowerCase())
      ) {
        return false;
      }
      if (filters.isActive !== undefined && vehicle.isActive !== filters.isActive) {
        return false;
      }
      if (filters.maintenanceDue) {
        if (!vehicle.nextServiceDue) return false;
        const dueDate = new Date(vehicle.nextServiceDue);
        if (dueDate > new Date()) return false;
      }
      return true;
    });
  }, [vehicles, filters]);

  const handleStatusUpdate = async (vehicleId: string, status: VehicleStatus) => {
    try {
      setError(null);
      const response = await vehicleService.updateVehicleStatus(vehicleId, { status });
      if (response.success) {
        onStatusUpdate?.(vehicleId, status);
      } else {
        setError(response.error?.message || 'Failed to update vehicle status');
      }
    } catch {
      setError('Failed to update vehicle status');
    }
  };

  const handleBulkStatusUpdate = async (status: VehicleStatus) => {
    setBulkUpdateLoading(true);
    try {
      setError(null);
      const updatePromises = selectedVehicleIds.map((id) =>
        vehicleService.updateVehicleStatus(id, { status }),
      );

      await Promise.all(updatePromises);
      setSelectedVehicleIds([]);
      onStatusUpdate?.(selectedVehicleIds[0] || '', status);
    } catch {
      setError('Failed to update vehicle statuses');
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  const handleVehicleSelection = (vehicleId: string, selected: boolean) => {
    if (selected) {
      setSelectedVehicleIds((prev) => [...prev, vehicleId]);
    } else {
      setSelectedVehicleIds((prev) => prev.filter((id) => id !== vehicleId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedVehicleIds(filteredVehicles.map((v) => v._id));
    } else {
      setSelectedVehicleIds([]);
    }
  };

  const isMaintenanceDue = (vehicle: Vehicle): boolean => {
    if (!vehicle.nextServiceDue) return false;
    const dueDate = new Date(vehicle.nextServiceDue);
    const today = new Date();
    const daysUntilDue = Math.ceil(
      (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    return daysUntilDue <= 7;
  };

  const getVehicleAvailability = (vehicleId: string) => {
    return availability.find((a) => a.carId === vehicleId);
  };

  const handleViewAvailability = (vehicleId: string) => {
    const vehicleAvail = getVehicleAvailability(vehicleId);
    if (vehicleAvail) {
      setSelectedAvailability(vehicleAvail);
      setShowAvailabilityModal(true);
    }
  };

  const formatNextAvailableDate = (date: Date | null | string) => {
    if (!date) return null;
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const columns = [
    {
      key: 'select',
      title: 'Select',
      render: (_: any, record: Vehicle) => (
        <input
          type="checkbox"
          checked={selectedVehicleIds.includes(record._id)}
          onChange={(e) => handleVehicleSelection(record._id, e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    {
      key: 'name',
      title: 'Vehicle',
      sortable: true,
      render: (_: any, record: Vehicle) => (
        <div>
          <div className="font-medium text-gray-900">{record.name}</div>
          <div className="text-sm text-gray-500">
            {record.brand} {record.model} ({record.year})
          </div>
          {record.licensePlate && (
            <div className="text-xs text-gray-400">{record.licensePlate}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (status: VehicleStatus, record: Vehicle) => (
        <div className="space-y-1">
          <VehicleStatusBadge status={status} />
          {isMaintenanceDue(record) && (
            <div className="text-xs text-orange-600 font-medium">Maintenance Due</div>
          )}
        </div>
      ),
    },
    {
      key: 'location',
      title: 'Location',
      sortable: true,
      render: (location: string) => location || 'Not specified',
    },
    {
      key: 'availability',
      title: 'Availability',
      sortable: false,
      render: (_: any, record: Vehicle) => {
        const total = record.totalQuantity || 0;
        const vehicleAvail = getVehicleAvailability(record._id);
        const activeBookings =
          vehicleAvail?.activeBookings || record.activeBookings || 0;
        const available = Math.max(0, total - activeBookings);
        const nextAvailDate = vehicleAvail?.nextAvailableDate;
        const hasBookings =
          vehicleAvail &&
          vehicleAvail.upcomingReturns &&
          vehicleAvail.upcomingReturns.length > 0;

        return (
          <div
            className={`space-y-1 ${hasBookings ? 'cursor-pointer' : ''}`}
            onClick={() => hasBookings && handleViewAvailability(record._id)}
            title={hasBookings ? 'Click to view booking schedule' : undefined}
          >
            <div className="text-sm font-semibold">
              {available}/{total}
            </div>
            {available > 0 ? (
              <div className="space-y-0.5">
                <div className="text-xs text-green-600 font-medium">{available} Available</div>
                {hasBookings && (
                  <div className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-800 hover:underline">
                    View Schedule
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-0.5">
                <div className="text-xs text-red-600 font-medium">Fully Booked</div>
                {nextAvailDate && (
                  <div className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-800 hover:underline">
                    Next: {formatNextAvailableDate(nextAvailDate)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'pricing',
      title: '7+ Days Rate',
      sortable: false,
      render: (_: any, record: Vehicle) => {
        const limitedSevenPlusRate = record.rates?.find((tier) => tier.minDays === 7);
        const unlimitedSevenPlusRate = record.unlimedRates?.find(
          (tier) => tier.minDays === 7,
        );

        return (
          <div className="space-y-1.5">
            <div>
              <div className="text-xs text-gray-600">Limited:</div>
              <div className="text-sm font-medium">
                {limitedSevenPlusRate ? `$${limitedSevenPlusRate.rate}/day` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Unlimited:</div>
              <div className="text-sm font-medium">
                {unlimitedSevenPlusRate
                  ? `$${unlimitedSevenPlusRate.rate}/day`
                  : 'N/A'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'totalBookings',
      title: 'Bookings',
      sortable: true,
      render: (bookings: number) => <div className="font-medium">{bookings || 0}</div>,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, record: Vehicle) => (
        <div className="flex flex-col space-y-2">
          <div className="flex space-x-2">
            <button
              onClick={() => onVehicleSelect?.(record)}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              View
            </button>
            <button
              onClick={() => onVehicleEdit?.(record)}
              className="text-green-600 hover:text-green-800 text-sm"
            >
              Edit
            </button>
          </div>
          <select
            value={record.status}
            onChange={(e) =>
              handleStatusUpdate(record._id, e.target.value as VehicleStatus)
            }
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            {Object.values(VehicleStatus).map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading vehicles...</span>
      </div>
    );
  }

  return (
    <div>
      <VehicleFilters
        filters={filters}
        onFiltersChange={setFilters}
        availableBrands={availableBrands}
        availableLocations={availableLocations}
      />

      <BulkActions
        selectedVehicleIds={selectedVehicleIds}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onClearSelection={() => setSelectedVehicleIds([])}
        isLoading={bulkUpdateLoading}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="text-red-800">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Fleet Vehicles ({filteredVehicles.length})
            </h3>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedVehicleIds.length === filteredVehicles.length && filteredVehicles.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                {columns.slice(1).map((column) => (
                  <th
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.title}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVehicles.map((vehicle) => (
                <tr key={vehicle._id} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 text-sm text-gray-900">
                      {column.render
                        ? column.render(vehicle[column.key as keyof Vehicle], vehicle)
                        : String(vehicle[column.key as keyof Vehicle] || '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <VehicleAvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        availability={selectedAvailability}
      />
    </div>
  );
};

export default VehicleListing;
